import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnInit } from '@angular/core';
import { Params, Router, RouterOutlet } from '@angular/router';
import { cultures, DynamicState, Environment, EnvironmentService, LocaleService, MainState, Page, SetDefaultLanguage, SetLogo, SetRoutePrefix, SetTitle, StyleService, Tenant, Translation, UtilityService } from '@cloud-and-service/portal-lib';
import { Select, Store } from '@ngxs/store';
import { NgHttpLoaderComponent } from 'ng-http-loader';
import { Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { CookiesComponent } from './components/cookies/cookies.component';
import { RouteInfo } from './models/routeinfo';
import { InfoService } from './services/info.service';
import { MetaService } from './services/meta.service';
import { RouterParams } from './services/router-params.service';
import { TenantService } from './services/tenant.service';
import { TranslationService } from './services/translation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    NgHttpLoaderComponent,
    CookiesComponent,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly environment: Environment;

  @HostListener('document:keypress', ['$event'])
  async handleKeyboardEvent(event: KeyboardEvent) {
    if (event.ctrlKey && event.shiftKey && event.code == 'KeyV') {
      var backendEnvironment = await this.infoService.backendEnvironment()
        .then(data => {
          return data.text;
        }).catch(
          () => {
            return '--';
          }
        );
      var backendVersion = await this.infoService.backendVersion()
        .then(data => {
          return data.text;
        }).catch(
          () => {
            return '--';
          }
        );

      var frontendEnvironment = this.environment.environment;
      var frontendVersion = this.environment.appVersion;

      var newLine = '\r';
      var msg = `Backend Env.: ${backendEnvironment}`;
      msg += newLine;
      msg += `Backend v.: ${backendVersion}`;
      msg += newLine;
      msg += `Frontend Env.: ${frontendEnvironment}`;
      msg += newLine;
      msg += `Frontend v.: ${frontendVersion}`;
      alert(msg);
    }
  }

  @Select(DynamicState.page) page$!: Observable<Page>;
  @Select(MainState.translations) translations$: Observable<Translation[]> | undefined;
  @Select(MainState.tenant) tenant$!: Observable<Tenant | null>;

  constructor(
    private localeService: LocaleService,
    private routerParams: RouterParams,
    private tenantService: TenantService,
    private utilityService: UtilityService,
    private metaService: MetaService,
    private infoService: InfoService,
    private store: Store,
    private translationService: TranslationService,
    private router: Router,
    private styleService: StyleService,
    environmentService: EnvironmentService
  ) {
    this.environment = environmentService.getEnvironment();
  }

  async ngOnInit(): Promise<void> {
    this.getPortalSetings();
    this.applyTenantThemeOnChange();
  }

  ngAfterViewInit(): void {
    // Belt-and-suspenders: after all child components are initialized and any
    // synchronous NGXS emissions have settled, re-apply the page theme if present.
    setTimeout(() => {
      const page = this.store.selectSnapshot(DynamicState.page);
      if (page?.themeOverride) {
        this.styleService.applyPageOverride(page.themeOverride);
      }
    }, 0);
  }

  title = '365Portal';

  /**
   * Detects the browser's locale and returns it if supported, otherwise returns null.
   * Extracts the language code (e.g., 'en' from 'en-US') and validates against supported locales.
   */
  private getBrowserLocale(): string | null {
    if (typeof navigator === 'undefined' || !navigator.language) {
      return null;
    }

    // Extract language code (e.g., 'en' from 'en-US' or 'EN-us')
    const browserLang = navigator.language.toLowerCase().split('-')[0];

    // Check if the detected language is supported
    const supportedLanguages = cultures.map(c => c.language);
    return supportedLanguages.includes(browserLang) ? browserLang : null;
  }

  getPortalSetings() {
    this.routerParams.params.subscribe(
      async (params: Params) => {

        const routePrefix = params['forsyning'];
        let heading: string = '';
        let logo: string = '';
        const staticLogo = window.location.origin + '/assets/img/fo-logo.svg';

        if (!routePrefix) {
          return;
        }

        this.store.dispatch(new SetRoutePrefix(routePrefix));

        await this.tenantService.getRouteInfo(routePrefix)
          .then((result: RouteInfo) => {

            if (!result) {
              return;
            }

            if (!result.isActive) {
              this.router.navigate(['/']);
              return;
            }

            if (!this.utilityService.isEmpty(result.loginHeading)) {
              heading = result.loginHeading;
              this.store.dispatch(new SetTitle(heading));
            } else {
              heading = 'Forsyning Online';
              this.store.dispatch(new SetTitle(heading));
            }

            if (!this.utilityService.isEmpty(result.logoUrl)) {
              logo = `${this.environment.storageUrl}/logos/${routePrefix.toLowerCase()}-logo${result.logoUrl}`;
              this.store.dispatch(new SetLogo(logo));
            } else {
              logo = 'assets/img/fo-logo.svg';
              this.store.dispatch(new SetLogo(logo));
            }

            if (result.locale) {
              this.store.dispatch(new SetDefaultLanguage(result.locale));
            } else {
              const browserLocale = this.getBrowserLocale();
              const defaultLocale = browserLocale || 'da';
              this.store.dispatch(new SetDefaultLanguage(defaultLocale));
            }

            if (result.theme) {
              // Only apply the pre-login portal theme if no tenant session is active.
              // After login the tenant's own theme is applied via applyTenantThemeOnChange().
              const currentTenant = this.store.selectSnapshot(MainState.tenant);
              if (!currentTenant) {
                this.styleService.applyResolvedTheme(result.theme);
              }
            }

            this.metaService.update(staticLogo, heading, 'Log ind');
          });
      });
  }

  private applyTenantThemeOnChange(): void {
    this.tenant$.pipe(
      map(tenant => tenant?.portal?.resolvedTheme ?? null),
      distinctUntilChanged(),
    ).subscribe(resolvedTheme => {
      if (resolvedTheme) {
        this.styleService.applyResolvedTheme(resolvedTheme);
      } else {
        this.styleService.clearTheme();
      }
    });
  }

}
